import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { DateTime } from 'luxon'
import clsx from 'clsx'
import Box from '@material-ui/core/Box'
import Link from '@material-ui/core/Link'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'
import FileUploadIcon from 'components/material-symbol/icons/FileUpload'
import MessageIcon from 'components/material-symbol/icons/Message'
import AccountRouterLink from 'components/account-router-link'
import { PageHeader } from 'components/page-header'
import RouterLinkButton from 'components/router-link-button'
import { centsToDollarsFormatted } from 'lib/utils/number'
import { useMyVirtualCoachingRequest } from './api-hooks'
import {
  trackVirtualCoachingStudentEvent,
  VIRTUAL_COACHING_STUDENT_EVENTS,
} from './analytics'
import CoachLine from './coach-line'
import RequestResponseCard from './request-response-card'
import StudentStatusChip, {
  getStatusChipClassName,
} from './request-status-chip'
import { RequestLoadError, RequestNotFound } from './request-state-messages'
import StepsList from './steps-list'
import { useStyles } from './styles'
import {
  formatDueDate,
  formatTurnaroundWithin,
  getCoachFirstName,
  getMessageCoachPath,
  getTransactionPath,
  getUploadPath,
  isHttpsUrl,
  REQUEST_STATUS,
  REQUESTS_PATH,
  STATUS_STEP,
} from './utils'

const CRUMBS = [{ label: 'Virtual Coaching', to: REQUESTS_PATH }]
const ALL_STEPS_DONE = 4

const getSteps = request => {
  const { coach, virtualCoaching } = request.lessonType || {}
  const turnaround = formatTurnaroundWithin(virtualCoaching?.turnaroundHours)
  const dueDate = formatDueDate(request.dueAt)
  let reviewCaption = `${getCoachFirstName(coach)} will analyze your swing${
    turnaround ? ` ${turnaround}` : ''
  }`
  if (request.status === REQUEST_STATUS.READY_TO_ANALYZE && dueDate) {
    reviewCaption = `${getCoachFirstName(
      coach,
    )} is analyzing your swing. Expected by ${dueDate}`
  }

  return [
    { title: 'Upload your videos', caption: 'Record your swing and upload it' },
    { title: 'Coach reviews', caption: reviewCaption },
    {
      title: 'Get feedback',
      caption: 'Receive detailed feedback and improvement tips',
    },
  ]
}

const ContextItem = ({ label, value }) => {
  const classes = useStyles()
  if (!value) return null
  return (
    <Box className={classes.contextRow}>
      <Typography className={classes.contextLabel}>{label}</Typography>
      <Typography className={classes.notes}>{value}</Typography>
    </Box>
  )
}

ContextItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
}

const SubmittedVideos = ({ request }) => {
  const classes = useStyles()
  const videos = (request.videoUrls || []).filter(isHttpsUrl)
  if (videos.length === 0) return null

  return (
    <section className={classes.card} aria-labelledby="your-videos-title">
      <Box className={classes.cardHeader}>
        <Typography
          id="your-videos-title"
          variant="h2"
          className={classes.cardTitleText}
        >
          Your videos
        </Typography>
      </Box>
      <Box className={`${classes.cardBody} ${classes.videoGrid}`}>
        {videos.map((url, index) => (
          <Box key={url} className={classes.videoTile}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              className={classes.video}
              src={url}
              poster={request.videoThumbnailUrls?.[index] || undefined}
              controls
              playsInline
              preload="metadata"
              aria-label={`Your swing video ${index + 1}`}
            />
          </Box>
        ))}
      </Box>
    </section>
  )
}

SubmittedVideos.propTypes = { request: PropTypes.object.isRequired }

const StatusSection = ({ request }) => {
  const classes = useStyles()
  const { status, dueAt, lessonType } = request
  const messageCoachPath = getMessageCoachPath(lessonType?.coach)
  const coachFirstName = getCoachFirstName(lessonType?.coach)
  const isPastDue = Boolean(dueAt) && DateTime.fromISO(dueAt) < DateTime.local()

  return (
    <section className={classes.card} aria-labelledby="request-status-title">
      <Box className={classes.cardHeader}>
        <Box className={classes.cardTitleBlock}>
          <Typography
            id="request-status-title"
            variant="h2"
            className={classes.cardTitleText}
          >
            Where things stand
          </Typography>
        </Box>
        <StudentStatusChip
          status={status}
          size="small"
          className={getStatusChipClassName(classes, status)}
        />
      </Box>
      <Box className={classes.cardBody}>
        <StepsList
          steps={getSteps(request)}
          currentStep={
            status === REQUEST_STATUS.COMPLETE
              ? ALL_STEPS_DONE
              : STATUS_STEP[status]
          }
          aria-label="Progress"
        />
        {status === REQUEST_STATUS.READY_TO_ANALYZE && isPastDue ? (
          <Typography className={classes.muted} style={{ marginTop: 16 }}>
            {coachFirstName} is still working on your analysis. Send a message
            if you would like an update.
          </Typography>
        ) : null}
        <Box className={classes.actionRow}>
          {status === REQUEST_STATUS.PURCHASED ? (
            <RouterLinkButton
              variant="contained"
              color="primary"
              to={getUploadPath(request.id)}
              startIcon={<FileUploadIcon />}
            >
              Upload your videos
            </RouterLinkButton>
          ) : null}
          {messageCoachPath ? (
            <RouterLinkButton
              variant="outlined"
              color="primary"
              to={messageCoachPath}
              startIcon={<MessageIcon />}
            >
              Message {coachFirstName}
            </RouterLinkButton>
          ) : null}
        </Box>
      </Box>
    </section>
  )
}

StatusSection.propTypes = { request: PropTypes.object.isRequired }

// "Order #…", linking to the purchase's transaction page when the backend
// returned its id; plain text otherwise. `AccountRouterLink` adds `return_to`
// so the transaction page's back link comes here.
const OrderReference = ({ request }) => {
  const classes = useStyles()
  const label = `Order #${request.orderNumber}`
  const path = getTransactionPath(request.transaction)
  if (!path) return label

  return (
    <Link
      component={AccountRouterLink}
      to={path}
      className={classes.orderLink}
      aria-label={`${label}, view transaction`}
    >
      {label}
    </Link>
  )
}

OrderReference.propTypes = {
  request: PropTypes.shape({
    orderNumber: PropTypes.string,
    transaction: PropTypes.shape({ id: PropTypes.string }),
  }).isRequired,
}

const RequestDetailContent = ({ request }) => {
  const classes = useStyles()
  const { lessonType, status } = request
  const hasContext = request.golfLevel || request.struggle || request.goal

  return (
    <>
      <CoachLine coach={lessonType?.coach} variant="member" />
      <Typography variant="body2" className={classes.detailMeta}>
        {centsToDollarsFormatted(lessonType?.priceInCents)}
        {request.orderNumber ? (
          <>
            {' · '}
            <OrderReference request={request} />
          </>
        ) : null}
      </Typography>

      {status === REQUEST_STATUS.COMPLETE ? (
        <RequestResponseCard
          request={request}
          title={`Feedback from ${getCoachFirstName(lessonType?.coach)}`}
        />
      ) : null}

      <StatusSection request={request} />
      <SubmittedVideos request={request} />

      {hasContext ? (
        <section className={classes.card} aria-labelledby="your-game-title">
          <Box className={classes.cardHeader}>
            <Typography
              id="your-game-title"
              variant="h2"
              className={classes.cardTitleText}
            >
              What you told your coach
            </Typography>
          </Box>
          <Box className={`${classes.cardBody} ${classes.contextGrid}`}>
            <ContextItem label="Golf level" value={request.golfLevel} />
            <ContextItem label="Struggling with" value={request.struggle} />
            <ContextItem label="Goal" value={request.goal} />
          </Box>
        </section>
      ) : null}
    </>
  )
}

RequestDetailContent.propTypes = { request: PropTypes.object.isRequired }

const RequestDetail = () => {
  const classes = useStyles()
  const { requestId } = useParams()
  const { data, loading, error, refetch } = useMyVirtualCoachingRequest(
    requestId,
  )
  const request = data?.myVirtualCoachingRequest

  useEffect(() => {
    if (!request?.id) return
    trackVirtualCoachingStudentEvent(
      VIRTUAL_COACHING_STUDENT_EVENTS.REQUEST_VIEWED,
      { requestId: request.id, status: request.status },
    )
    // One view per request, not per refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id])

  let content
  if (loading && !request) {
    content = <LinearProgress color="primary" aria-label="Loading" />
  } else if (error && !request) {
    content = <RequestLoadError error={error} onRetry={() => refetch()} />
  } else if (!request) {
    content = <RequestNotFound />
  } else {
    content = <RequestDetailContent request={request} />
  }

  return (
    <Box className={classes.memberLayout}>
      <PageHeader
        title={request?.lessonType?.title || 'Virtual Coaching'}
        crumbs={CRUMBS}
      />
      <Box className={clsx(classes.clubPage, classes.memberPage)}>
        {content}
      </Box>
    </Box>
  )
}

export default RequestDetail
